/**
 * SF-4779 — "Security" tab on the company page (epic SF-4775).
 *
 * Starts the place for company security settings (more move here from the
 * web app later):
 *  - Require two-step sign-in for everyone who signs in with a password
 *    (`twoFactorAuthentication`), saved on its own through the page's
 *    single-field saver (one save at a time per company). Both directions ask
 *    first. Single sign-on and Google sign-ins are never asked for a code.
 *  - The company's people and their two-step status, with Reset (for a lost
 *    phone). Reset clears their two-step; if the company requires it they set
 *    it up again at their next sign-in.
 *
 * While the platform flag two_step_signin_available is off (the list reply's
 * `available` is not true; missing counts as off) the switch can be turned
 * off but not on, with a note; a 403 TWO_STEP_NOT_AVAILABLE on save shows the
 * same note and reloads. Resets are unaffected.
 *
 * Changing anything needs admin:companies:write and the company on the staff
 * member's list; without it the switch is read-only and Reset is not shown.
 * The server checks the same rights.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIdentity } from '@refinedev/core';
import {
  Alert,
  Button,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FlagSwitch } from '../FlagSwitch';
import type { PendingSave, SaveFieldOptions } from '../useSaveCompanyField';
import type { Company } from '../../list';
import {
  TWO_STEP_FIELD,
  TWO_STEP_PAGE_SIZE,
  TWO_STEP_RESET_SELF_TEXT,
  isTwoStepNotAvailable,
  parseTwoStepList,
  twoStepErrorMessage,
  twoStepResetArgs,
  useTwoStepReset,
  useTwoStepUsers,
  type TwoStepList,
  type TwoStepUser,
} from '../../api/twoStepClient';

import {
  TWO_STEP_SWITCH_LABEL,
  TWO_STEP_CONFIRM_ON,
  TWO_STEP_CONFIRM_OFF,
  TWO_STEP_RESET_CONFIRM,
  TWO_STEP_LOAD_FAILED,
  TWO_STEP_NOT_AVAILABLE_TEXT,
} from './securityText';

const { Text, Title } = Typography;

const METHOD_LABEL: Record<string, string> = { app: 'Authenticator app', email: 'Email code' };

type Saver = {
  save: <K extends keyof Company>(
    field: K,
    value: Company[K],
    successText: string,
    options?: SaveFieldOptions,
  ) => Promise<boolean>;
  pending: PendingSave | null;
  busy?: boolean;
};

export interface SecurityTabProps {
  company: Company;
  companyId: string;
  /** admin:companies:write and the company on the staff member's list. */
  canWrite: boolean;
  permissionsLoading: boolean;
  /** Shown as the switch's tooltip when it is read-only. */
  readOnlyReason?: string | null;
  saver: Saver;
}

export const SecurityTab: React.FC<SecurityTabProps> = ({
  company,
  companyId,
  canWrite,
  permissionsLoading,
  readOnlyReason,
  saver,
}) => {
  const [page, setPage] = useState(1);
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');
  const [resetting, setResetting] = useState<string | null>(null);

  const query = useTwoStepUsers(companyId, {
    page,
    enabled: enabledFilter || undefined,
    search,
  });
  // The answer for the page/filter now asked for (not the previous key's rows
  // kept on screen while it loads).
  const { data, isSuccess, isPreviousData } = query;
  const fresh: TwoStepList | null = useMemo(
    () => (isSuccess && !isPreviousData ? parseTwoStepList(data?.data) : null),
    [data, isSuccess, isPreviousData],
  );
  // Last list that loaded for this company: a failed new page/filter has no data
  // of its own, so the table keeps these rows with the "could not refresh" note.
  const [lastGood, setLastGood] = useState<{ companyId: string; list: TwoStepList } | null>(null);
  useEffect(() => {
    if (fresh) setLastGood({ companyId, list: fresh });
  }, [companyId, fresh]);
  const list: TwoStepList | null =
    parseTwoStepList(data?.data) ?? (lastGood?.companyId === companyId ? lastGood.list : null);

  // A reset (or anything else) can shrink the list under the page on screen:
  // step back to the last page that still has rows, keeping search and filter.
  const freshTotal = fresh?.totalCount;
  useEffect(() => {
    if (freshTotal === undefined) return;
    const lastPage = Math.max(1, Math.ceil(freshTotal / TWO_STEP_PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [freshTotal, page]);

  const { mutateAsync: resetMutate } = useTwoStepReset();

  // sf-api finds the signed-in staff member by their email (not the auth
  // service's id) and refuses to reset their own account, so match on email.
  const { data: identity } = useGetIdentity<{ email?: string }>();
  const myEmail = identity?.email?.trim().toLowerCase();
  const isMe = (u: TwoStepUser) => !!myEmail && u.email?.trim().toLowerCase() === myEmail;

  const pending = saver.pending?.field === TWO_STEP_FIELD ? (saver.pending.value as boolean) : null;
  const required = company.twoFactorAuthentication === true;

  // Platform flag (contract v3). Unknown until the list loads: the switch stays
  // off-only meanwhile, but the note waits for an answer. A 403 on save holds
  // the note until a fresh read says the flag is on.
  const [refusedOn, setRefusedOn] = useState(false);
  useEffect(() => {
    if (fresh?.available === true) setRefusedOn(false);
  }, [fresh]);
  const available = list?.available === true && !refusedOn;
  const showNotAvailable = refusedOn || (list !== null && !list.available);
  const onlyOff = !available && !required && pending === null;

  // Held from the click until the list has refreshed: React state alone lets a
  // double-click on the confirmation through before the next render.
  const resetLock = useRef(false);
  const onReset = async (user: TwoStepUser) => {
    if (resetLock.current) return;
    resetLock.current = true;
    setResetting(user._id);
    try {
      await resetMutate(twoStepResetArgs(companyId, user._id));
      message.success(`Two-step sign-in reset for ${user.name || user.email || 'this person'}.`);
    } catch (err) {
      message.error(twoStepErrorMessage(err, 'Could not reset two-step sign-in.'));
    } finally {
      try {
        await query.refetch();
      } finally {
        resetLock.current = false;
        setResetting(null);
      }
    }
  };

  const columns: ColumnsType<TwoStepUser> = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v?: string) => v || '—' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v?: string) => v || '—' },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (v?: string) => v || '—' },
    {
      title: 'Two-step',
      key: 'two_step',
      render: (_: unknown, u) =>
        u.two_step?.enabled ? (
          <Space direction="vertical" size={0}>
            <Tag color="green">On</Tag>
            <Text type="secondary">
              {METHOD_LABEL[u.two_step.method ?? ''] ?? u.two_step.method ?? '—'}
              {u.two_step.enabled_at
                ? ` · since ${new Date(u.two_step.enabled_at).toLocaleDateString()}`
                : ''}
            </Text>
          </Space>
        ) : (
          <Tag>Off</Tag>
        ),
    },
    ...(canWrite
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (_: unknown, u: TwoStepUser) =>
              u.two_step?.enabled && isMe(u) ? (
                // Wrapped in a span: a disabled Button swallows the hover a Tooltip needs.
                <Tooltip title={TWO_STEP_RESET_SELF_TEXT}>
                  <span data-testid="two-step-reset-self">
                    <Button size="small" danger disabled>
                      Reset
                    </Button>
                  </span>
                </Tooltip>
              ) : u.two_step?.enabled ? (
                <Popconfirm
                  title={TWO_STEP_RESET_CONFIRM}
                  okText="Reset"
                  okButtonProps={{
                    danger: true,
                    loading: resetting === u._id,
                    disabled: resetting !== null && resetting !== u._id,
                  }}
                  disabled={resetting !== null}
                  onConfirm={() => onReset(u)}
                >
                  <Button
                    size="small"
                    danger
                    loading={resetting === u._id}
                    disabled={resetting !== null && resetting !== u._id}
                  >
                    Reset
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="start">
        <FlagSwitch
          testId="two-step-required-toggle"
          ariaLabel={TWO_STEP_SWITCH_LABEL}
          checked={required}
          pendingValue={pending}
          loading={permissionsLoading}
          disabled={
            !canWrite || permissionsLoading || (saver.busy === true && pending === null) || onlyOff
          }
          disabledReason={
            !permissionsLoading && !canWrite
              ? readOnlyReason
              : onlyOff && showNotAvailable
                ? TWO_STEP_NOT_AVAILABLE_TEXT
                : null
          }
          confirmOn={TWO_STEP_CONFIRM_ON}
          confirmOff={TWO_STEP_CONFIRM_OFF}
          onChange={(next) =>
            void saver.save(
              TWO_STEP_FIELD,
              next,
              next ? 'Two-step sign-in is now required' : 'Two-step sign-in is no longer required',
              {
                onError: (err) => {
                  if (!isTwoStepNotAvailable(err)) return false;
                  setRefusedOn(true);
                  void query.refetch();
                  return true;
                },
              },
            )
          }
        />
        <Space direction="vertical" size={0}>
          <Text strong>{TWO_STEP_SWITCH_LABEL}</Text>
          <Text type="secondary">Single sign-on and Google sign-ins are not affected.</Text>
          {showNotAvailable ? (
            <Text type="warning">
              {TWO_STEP_NOT_AVAILABLE_TEXT}
            </Text>
          ) : null}
        </Space>
      </Space>

      <div>
        <Title level={5}>People and their two-step sign-in</Title>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search
            allowClear
            placeholder="Search name or email"
            aria-label="Search people"
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
          <Select
            aria-label="Two-step filter"
            data-testid="two-step-filter"
            value={enabledFilter}
            onChange={(v) => {
              setEnabledFilter(v);
              setPage(1);
            }}
            style={{ width: 160 }}
            options={[
              { label: 'Everyone', value: '' },
              { label: 'Two-step on', value: 'true' },
              { label: 'Two-step off', value: 'false' },
            ]}
          />
        </Space>
        {query.isError ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={list ? TWO_STEP_LOAD_FAILED : 'Could not load the list.'}
            action={
              <Button size="small" onClick={() => void query.refetch()}>
                Try again
              </Button>
            }
          />
        ) : null}
        <Table<TwoStepUser>
          rowKey="_id"
          size="small"
          columns={columns}
          dataSource={list?.users ?? []}
          loading={query.isFetching === true}
          pagination={{
            current: page,
            pageSize: TWO_STEP_PAGE_SIZE,
            total: list?.totalCount ?? 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </div>
    </Space>
  );
};
